import CopyToClipboard from '@/components/Button/CopyToClipboard';
import { useRegenerateApiKey } from '@/http/queries/useRegenerateApiKey';
import { T } from '@/i18n/T';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import LoadingButton from '@components/Button/LoadingButton';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Typography,
} from '@eye-able/ui';
import { RefreshCw } from 'lucide-react';

interface GenerateApiKeyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function GenerateApiKeyDialog({
  open,
  onOpenChange,
}: GenerateApiKeyDialogProps) {
  const { t } = useInlineTranslation();
  const { mutate, reset, isPending, data } = useRegenerateApiKey();
  const apiKey = data?.apiKey;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) reset();
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/* Persistent while the key shows, so a stray click outside cannot discard it. */}
      <DialogContent
        maxWidth='sm'
        className='eye:w-full'
        persistent={isPending || !!apiKey}
      >
        {apiKey ? (
          <>
            <DialogTitle>
              <T k='newApiKeyTitle' />
            </DialogTitle>
            <Alert
              variant='warning'
              title={<T k='apiKeyOnceWarning' />}
              className='eye:my-2xs'
            >
              <div className='eye:flex eye:items-center eye:gap-2xs eye:rounded-md eye:border eye:border-grey-20 eye:bg-grey-5 eye:px-2xs eye:py-3xs'>
                <Typography
                  variant='body-s'
                  className='eye:flex-1 eye:break-all eye:font-mono'
                >
                  {apiKey}
                </Typography>
                <CopyToClipboard text={apiKey} />
              </div>
            </Alert>
            <DialogActions>
              <Button
                type='button'
                variant='outline'
                onClick={() => handleOpenChange(false)}
              >
                {t('close')}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>
              <T k='generateApiKey' />
            </DialogTitle>
            <DialogDescription className='eye:my-2xs'>
              <T k='generateApiKeyConfirm' />
            </DialogDescription>
            <DialogActions>
              <Button
                type='button'
                variant='outline'
                disabled={isPending}
                onClick={() => handleOpenChange(false)}
              >
                {t('cancel')}
              </Button>
              <LoadingButton
                type='button'
                variant='yellow'
                loading={isPending}
                onClick={() => mutate()}
              >
                <RefreshCw aria-hidden />
                {t('generateApiKey')}
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default GenerateApiKeyDialog;
